import {
  ItemType,
  RetentionType,
  AbutmentType,
  TeethShadeSide,
  FixedProsthesisShadeType,
  PartialAndFullDentureProsthesisShadeType,
  ItemShadeType,
  ProductPreferenceType,
  LayType,
  WaxUpType,
} from "../types/enums";
import {
  RemovableItem,
  ImplantItem,
  AnatomyItem,
  Item,
  Product,
  ProductPreferences,
  MultiShadeInfo,
} from "../types/interfaces";
import {
  isRemovableItemType,
  getItemTypeLabel,
  findUserPreference,
} from "./product.utils";
import {
  translateItemType,
  translateInlayCoreMaterial,
  translateItemMaterial,
} from "./translation.utils";
import { isBeforeDay } from "./date.utils";
import { getWaxUpTypeSelection } from "@jasper/client-models";

const clavetedInlayCoreMaterials = ["METAL_NON_PRECIOUS", "TITANIUM"];

export const countItemTeeth = (
  itemType: ItemType,
  itemTeeth: number[],
  productTeeth: number[]
) => {
  if (itemType === ItemType.BRIDGE_IMPLANT) {
    /*
     * Count bridge pontic as teeth for bridge implant
     */
    return (productTeeth ?? []).length;
  }
  return (itemTeeth ?? []).length;
};

export const isAllMissingTeeth = (item: RemovableItem): boolean => {
  const FULL_ARCH_SIZE = 14;
  return (
    item.teeth.length >= FULL_ARCH_SIZE &&
    item.partialDenture?.missingTeeth != null &&
    item.teeth.every(t => item.partialDenture.missingTeeth.includes(t))
  );
};

export const deduceItemTypeFromOptions = (
  data: {
    isInlayCoreOnly: boolean;
    isRichmondCrown: boolean;
    layType?: LayType;
  },
  product: Product
) => {
  if (data.isInlayCoreOnly) {
    return ItemType.INLAY_CORE;
  }
  if (data.isRichmondCrown) {
    return ItemType.RICHMOND_CROWN;
  }
  if (product.productType === ItemType.LAY) {
    return data.layType ?? product.productType;
  }
  return product?.productType ?? undefined;
};

export const getItemsOfBridgeImplant = (
  implantItems: ImplantItem[] = []
): {
  abutment: ImplantItem | null;
  bridgeImplant: ImplantItem | null;
  bridgePontic: ImplantItem | null;
} => {
  const bridgePonticItem = (implantItems ?? []).find(
    (item: ImplantItem) => item.itemType === ItemType.BRIDGE_PONTIC
  );
  const bridgeImplantItem = (implantItems ?? []).find(
    (item: ImplantItem) => item.itemType === ItemType.BRIDGE_IMPLANT
  );
  const abutment = (implantItems ?? []).find(
    (item: ImplantItem) => item.itemType === ItemType.IMPLANT
  );
  return {
    abutment: bridgeImplantItem ? abutment : null,
    bridgeImplant: bridgeImplantItem ?? null,
    bridgePontic: bridgeImplantItem ? bridgePonticItem : null,
  };
};

export const excludePonticFromBridgeImplantTeeth = (
  bridgeImplantTeeth: number[],
  bridePonticTeeth: number[]
) => {
  if (!bridePonticTeeth) {
    return bridgeImplantTeeth;
  }
  return (bridgeImplantTeeth ?? []).filter(
    (teeth: number) => !bridePonticTeeth.includes(teeth)
  );
};

export const getItemCrownOrSplintedCrownFromProduct = (
  anatomyItems: AnatomyItem[]
): AnatomyItem => {
  const crown = (anatomyItems ?? []).find(
    (item: AnatomyItem) => item.itemType === ItemType.CROWN
  );
  if (crown) {
    return crown;
  }
  return (anatomyItems ?? []).find(
    (item: AnatomyItem) => item.itemType === ItemType.SPLINTED_CROWN
  );
};

export const getFixedProsthesisPreferences = (
  productType: ItemType,
  productPreferences
) => {
  const fixedProsthesisPreferences =
    productPreferences?.fixedProsthesisPreferences;
  let preferenceItemType = productType;
  if (
    productType === ItemType.LAY ||
    productType === ItemType.INLAY ||
    productType === ItemType.OVERLAY ||
    productType === ItemType.ONLAY
  ) {
    preferenceItemType = ItemType.LAY;
  }
  if (productType === ItemType.POST_AND_CORE_RETAINED_CROWN) {
    preferenceItemType = ItemType.INLAY_CORE;
  }
  if (fixedProsthesisPreferences) {
    return (
      fixedProsthesisPreferences.find(
        itemPreference => itemPreference.itemType === preferenceItemType
      ) ?? null
    );
  } else {
    return null;
  }
};

export const findFullDentureSavedItemInProducts = (products: Product[]) => {
  if ((products ?? []).length === 0) return undefined;
  for (const i in products) {
    if ((products[i]?.removableItem ?? []).length === 0) continue;
    const fullDentureItem = products[i].removableItem.find(
      (item: { itemType: ItemType }) => isFullDentureType(item.itemType)
    );
    if (fullDentureItem) {
      return fullDentureItem;
    }
  }
  return undefined;
};

export const getShadeTypeAvailableByItemType = (
  isRemovableItem: boolean,
  itemType: ItemType
): string[] => {
  if (
    itemType &&
    (itemType === ItemType.PARTIAL_DENTURE ||
      itemType === ItemType.FULL_DENTURE ||
      itemType === ItemType.FULL_DENTURE_WAX_BITE ||
      itemType === ItemType.IMMEDIATE_DENTURE)
  ) {
    return Object.keys(PartialAndFullDentureProsthesisShadeType);
  }
  if (isRemovableItem) {
    return Object.keys(PartialAndFullDentureProsthesisShadeType);
  }
  return Object.keys(FixedProsthesisShadeType);
};

export const getGingivaShadeFromAllShades = (
  allShades: { shadeType: ItemShadeType; id: string; label: string }[]
): { value: string; label: string }[] => {
  return allShades
    .filter(
      (shade: { shadeType: ItemShadeType }) =>
        shade.shadeType === ItemShadeType.GINGIVA
    )
    .map((shade: { id: string; label: string }) => ({
      value: shade.id,
      label: shade.label,
    }));
};

export const isFullDentureType = (type: ItemType): boolean =>
  [
    ItemType.FULL_DENTURE,
    ItemType.FULL_DENTURE_WAX_BITE,
    ItemType.IMMEDIATE_DENTURE,
  ].includes(type);

export const deduceFinalItemTypeOfFullDenture = (
  productSpecificType: ItemType,
  newFullDentureStep: number,
  isReplacement: boolean
) => {
  if (productSpecificType === ItemType.IMMEDIATE_DENTURE) {
    return ItemType.IMMEDIATE_DENTURE;
  } else if (
    productSpecificType === ItemType.FULL_DENTURE &&
    newFullDentureStep === 1 &&
    !isReplacement
  ) {
    return ItemType.FULL_DENTURE_WAX_BITE;
  }
  return ItemType.FULL_DENTURE;
};

export const getTeethsFromUpperArch = (teeths: number[]): number[] => {
  if (!teeths || teeths.length === 0) {
    return [];
  }
  return teeths.reduce((acc: number[], teeth: number) => {
    if (teeth <= 28) {
      acc.push(teeth);
    }
    return acc;
  }, []);
};

export const getTeethsFromLowerArch = (teeths: number[]): number[] => {
  if (!teeths || teeths.length === 0) {
    return [];
  }
  return teeths.reduce((acc: number[], teeth: number) => {
    if (teeth >= 31) {
      acc.push(teeth);
    }
    return acc;
  }, []);
};

export const sortTeethAsc = (teeth?: number[]): number[] =>
  [...(teeth ?? [])].sort((a, b) => a - b);

export const hasMaxillaryTeeth = (teeth: number[]): boolean =>
  (teeth ?? []).some(t => t <= 28);
export const hasMandibularTeeth = (teeth: number[]): boolean =>
  (teeth ?? []).some(t => t >= 31);

export const checkHasGenericImplant = order => {
  const products = order?.products ?? [];
  const implantItems = products.reduce((acc, product) => {
    if ((product?.implantItem ?? []).length > 0) {
      acc.push(...product.implantItem);
    }
    return acc;
  }, []);
  if (implantItems.length === 0) {
    return false;
  }
  const hasGenericImplant = (implantItems ?? []).find(
    item =>
      item.abutmentType === AbutmentType.STANDARD ||
      item.abutmentType === AbutmentType.STANDARD_GENERIC
  );
  return hasGenericImplant ?? false;
};

export const shouldWarnAboutAbutmentSwap = (
  order: Parameters<typeof checkHasGenericImplant>[0],
  retainedDeliveryDate: Date | string | null | undefined,
  optimalDeliveryDate: Date | string | null | undefined
): boolean => {
  if (!checkHasGenericImplant(order)) {
    return false;
  }
  if (!retainedDeliveryDate || !optimalDeliveryDate) {
    return false;
  }
  return isBeforeDay(
    new Date(retainedDeliveryDate),
    new Date(optimalDeliveryDate)
  );
};

export const formatChoicesData = (
  rawData: any,
  claveted?: string
): { value: string; label: string; shadeType: ItemShadeType }[] => {
  let data = rawData;
  if (claveted) {
    data = data.filter((item: any) =>
      clavetedInlayCoreMaterials.includes(item.id)
    );
  }
  return data
    .map((item: any) => ({
      label: item.label,
      value: item.id,
      shadeType: item.shadeType,
      ...item,
    }))
    .sort(
      (
        a: { value: string; label: string; shadeType: ItemShadeType },
        b: { value: string; label: string; shadeType: ItemShadeType }
      ) => a.value.localeCompare(b.value)
    );
};

export const checkisInlayCoreOnly = (product: Product) => {
  if (!product) {
    return false;
  }
  if (product.productType !== ItemType.INLAY_CORE) {
    return false;
  }
  if (
    (product?.anatomyItem ?? []).find(
      (item: AnatomyItem) =>
        item.itemType === ItemType.CROWN ||
        item.itemType === ItemType.SPLINTED_CROWN
    )
  ) {
    return false;
  }

  return true;
};

export const deduceShadeType = (
  shadeId: FixedProsthesisShadeType | PartialAndFullDentureProsthesisShadeType,
  user_id: string,
  accountProductPreferences: {
    dentist: { id: string };
    productPreferenceType: ProductPreferenceType;
  }[],
  isMultiShade: boolean = false,
  multiShadeInfo: MultiShadeInfo[] = []
) => {
  if (isMultiShade) {
    /*
     * Only check base shade cause
     * it's the only mandatory shade on multishade
     */
    const baseShade =
      multiShadeInfo.find(
        multiShadeInfo => multiShadeInfo.teethShadeSide === TeethShadeSide.BASE
      )?.itemShade?.shadeType ?? undefined;
    if (baseShade) {
      return baseShade;
    }
  }
  if (!shadeId) {
    return (
      findUserPreference(
        user_id,
        accountProductPreferences as ProductPreferences[]
      )?.shadeType ?? FixedProsthesisShadeType.VITA_CLASSIC
    );
  }

  const SHADE_TYPE_MATCHERS: Record<string, ItemShadeType> = {
    [ItemShadeType.VITA_CLASSIC]: ItemShadeType.VITA_CLASSIC,
    [ItemShadeType.VITA_3D_MASTER]: ItemShadeType.VITA_3D_MASTER,
    [ItemShadeType.SR_VIVODENT_SPE]: ItemShadeType.SR_VIVODENT_SPE,
    [ItemShadeType.CHROMASCOP]: ItemShadeType.CHROMASCOP,
  };

  for (const key in SHADE_TYPE_MATCHERS) {
    if (shadeId.toLowerCase().includes(key.toLowerCase())) {
      return SHADE_TYPE_MATCHERS[key];
    }
  }

  return FixedProsthesisShadeType.VITA_3D_MASTER;
};

export const getTeethSideLabel = (teethSide: string, translated?: boolean) => {
  if (!translated) {
    switch (teethSide) {
      case TeethShadeSide.GINGIVAL:
        return "Tiers cervical";
      case TeethShadeSide.BASE:
        return "Tiers médian";
      case TeethShadeSide.INCISAL:
        return "Tiers incisal";
    }
  }
  return teethSide;
};

export const getItemMultiShadeLabel = (item, translated?) => {
  return (item?.multiShadeInfo ?? []).reduce(
    (acc, shade) =>
      acc +
      `- ${getTeethSideLabel(shade?.teethShadeSide, translated)}: ${shade?.itemShade?.label}\n`,
    ""
  );
};

const AbutmentTypeChoices = [
  { value: AbutmentType.STANDARD, label: "Standard" },
  { value: AbutmentType.CUSTOM, label: "Personnalisé" },
];

export const StandardAbutmentTypeChoices = [
  { value: AbutmentType.STANDARD, label: "Pilier original - fabriquant" },
  { value: AbutmentType.STANDARD_GENERIC, label: "Pilier standard générique" },
];

export const INLAY_CORE_SCAN_POST = {
  ANATOMICAL: { value: "ANATOMICAL", label: "Anatomique" },
};

export const scanPosts = [
  {
    type: "PIVOMATIC",
    diameter: "1.2",
    length: "SHORT",
    value: "PIVOMATIC_1.2_SHORT",
    label: "PS 1.2 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.3",
    length: "SHORT",
    value: "PIVOMATIC_1.3_SHORT",
    label: "PS 1.3 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.4",
    length: "SHORT",
    value: "PIVOMATIC_1.4_SHORT",
    label: "PS 1.4 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.5",
    length: "SHORT",
    value: "PIVOMATIC_1.5_SHORT",
    label: "PS 1.5 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.6",
    length: "SHORT",
    value: "PIVOMATIC_1.6_SHORT",
    label: "PS 1.6 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.7",
    length: "SHORT",
    value: "PIVOMATIC_1.7_SHORT",
    label: "PS 1.7 (Court)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.2",
    length: "LONG",
    value: "PIVOMATIC_1.2_LONG",
    label: "PL 1.2 (Long)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.3",
    length: "LONG",
    value: "PIVOMATIC_1.3_LONG",
    label: "PL 1.3 (Long)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.4",
    length: "LONG",
    value: "PIVOMATIC_1.4_LONG",
    label: "PL 1.4 (Long)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.5",
    length: "LONG",
    value: "PIVOMATIC_1.5_LONG",
    label: "PL 1.5 (Long)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.6",
    length: "LONG",
    value: "PIVOMATIC_1.6_LONG",
    label: "PL 1.6 (Long)",
  },
  {
    type: "PIVOMATIC",
    diameter: "1.7",
    length: "LONG",
    value: "PIVOMATIC_1.7_LONG",
    label: "PL 1.7 (Long)",
  },
  {
    type: "APOL",
    diameter: "1.4",
    length: "SHORT",
    value: "APOL_1.4_SHORT",
    label: "AS 1.4 (Court)",
  },
  {
    type: "APOL",
    diameter: "1.6",
    length: "SHORT",
    value: "APOL_1.6_SHORT",
    label: "AS 1.6 (Court)",
  },
  {
    type: "APOL",
    diameter: "1.7",
    length: "SHORT",
    value: "APOL_1.7_SHORT",
    label: "AS 1.7 (Court)",
  },
  {
    type: "APOL",
    diameter: "1.2",
    length: "LONG",
    value: "APOL_1.2_LONG",
    label: "AL 1.2 (Long)",
  },
  {
    type: "APOL",
    diameter: "1.4",
    length: "LONG",
    value: "APOL_1.4_LONG",
    label: "AL 1.4 (Long)",
  },
  {
    type: "APOL",
    diameter: "1.6",
    length: "LONG",
    value: "APOL_1.6_LONG",
    label: "AL 1.6 (Long)",
  },
  {
    type: "APOL",
    diameter: "1.7",
    length: "LONG",
    value: "APOL_1.7_LONG",
    label: "AL 1.7 (Long)",
  },
  {
    type: "EXOTEC",
    diameter: "1.2",
    length: "SHORT",
    value: "EXOTEC_1.2_YELLOW_SHORT",
    label: "Exotec 1.2 Jaune (Court)",
  },
  {
    type: "EXOTEC",
    diameter: "1.2",
    length: "LONG",
    value: "EXOTEC_1.2_YELLOW_LONG",
    label: "Exotec 1.2 Jaune (Long)",
  },
  {
    type: "EXOTEC",
    diameter: "1.4",
    length: "SHORT",
    value: "EXOTEC_1.4_PURPLE_SHORT",
    label: "Exotec 1.4 Violet (Court)",
  },
  {
    type: "EXOTEC",
    diameter: "1.4",
    length: "LONG",
    value: "EXOTEC_1.4_PURPLE_LONG",
    label: "Exotec 1.4 Violet (Long)",
  },
  {
    type: "EXOTEC",
    diameter: "1.6",
    length: "SHORT",
    value: "EXOTEC_1.6_BLUE_SHORT",
    label: "Exotec 1.6 Bleu (Court)",
  },
  {
    type: "EXOTEC",
    diameter: "1.6",
    length: "LONG",
    value: "EXOTEC_1.6_BLUE_LONG",
    label: "Exotec 1.6 Bleu (Long)",
  },
  {
    type: "EXOTEC",
    diameter: "1.7",
    length: "SHORT",
    value: "EXOTEC_1.7_GREY_SHORT",
    label: "Exotec 1.7 Gris (Court)",
  },
  {
    type: "EXOTEC",
    diameter: "1.7",
    length: "LONG",
    value: "EXOTEC_1.7_GREY_LONG",
    label: "Exotec 1.7 Gris (Long)",
  },
  {
    type: "PYXEL",
    diameter: "1.2",
    length: "SHORT",
    value: "PYXEL_1.2_WHITE_SHORT",
    label: "FSB Blanc Court (D1.2)",
  },
  {
    type: "PYXEL",
    diameter: "1.2",
    length: "LONG",
    value: "PYXEL_1.2_WHITE_LONG",
    label: "FSB Blanc Long (D1.2)",
  },
  {
    type: "PYXEL",
    diameter: "1.3",
    length: "SHORT",
    value: "PYXEL_1.3_YELLOW_SHORT",
    label: "FSB Jaune Court (D1.3)",
  },
  {
    type: "PYXEL",
    diameter: "1.3",
    length: "LONG",
    value: "PYXEL_1.3_YELLOW_LONG",
    label: "FSB Jaune Long (D1.3)",
  },
  {
    type: "PYXEL",
    diameter: "1.4",
    length: "SHORT",
    value: "PYXEL_1.4_RED_SHORT",
    label: "FSB Rouge Court (D1.4)",
  },
  {
    type: "PYXEL",
    diameter: "1.4",
    length: "LONG",
    value: "PYXEL_1.4_RED_LONG",
    label: "FSB Rouge Long (D1.4)",
  },
  {
    type: "PYXEL",
    diameter: "1.5",
    length: "SHORT",
    value: "PYXEL_1.5_BLUE_SHORT",
    label: "FSB Bleu Court (D1.5)",
  },
  {
    type: "PYXEL",
    diameter: "1.5",
    length: "LONG",
    value: "PYXEL_1.5_BLUE_LONG",
    label: "FSB Bleu Long (D1.5)",
  },
  {
    type: "PYXEL",
    diameter: "1.6",
    length: "SHORT",
    value: "PYXEL_1.6_GREEN_SHORT",
    label: "FSB Vert Court (D1.6)",
  },
  {
    type: "PYXEL",
    diameter: "1.6",
    length: "LONG",
    value: "PYXEL_1.6_GREEN_LONG",
    label: "FSB Vert Long (D1.6)",
  },
  {
    type: "PYXEL",
    diameter: "1.7",
    length: "SHORT",
    value: "PYXEL_1.7_BLACK_SHORT",
    label: "FSB Noir Court (D1.7)",
  },
  {
    type: "PYXEL",
    diameter: "1.7",
    length: "LONG",
    value: "PYXEL_1.7_BLACK_LONG",
    label: "FSB Noir Long (D1.7)",
  },
];

export const getItemMaterialLabel = (
  item: AnatomyItem | ImplantItem | RemovableItem
) => {
  let ret = "";
  if (!item.itemMaterial?.label) {
    return "";
  }
  if (
    item.itemType === ItemType.IMPLANT ||
    item.itemType === ItemType.SPLINTED_CROWN_IMPLANT
  ) {
    ret = ret + "Couronne ";
  }
  if (item.itemMaterial?.label) {
    return ret + item.itemMaterial.label;
  }
};

export const getItemInlayCoreMaterialLabel = (
  item: AnatomyItem,
  shouldTranslate: boolean = false
) => {
  if (
    item.itemType === ItemType.INLAY_CORE ||
    item.itemType === ItemType.POST_AND_CORE_RETAINED_CROWN ||
    item.itemType === ItemType.RICHMOND_CROWN
  ) {
    if (shouldTranslate) {
      return `Post and core material: ${translateInlayCoreMaterial(item?.inlayCoreMaterial?.id)}`;
    }
    return item.inlayCoreMaterial?.label
      ? `${item.itemType === ItemType.INLAY_CORE ? "Inlay-core" : "Tenon"} en ${item.inlayCoreMaterial.label}`
      : "";
  }
  return "";
};

export const getItemInlayCoreHasClavetteLabel = (
  item: AnatomyItem,
  shouldTranslate: boolean = false
) => {
  if (shouldTranslate) {
    return item.inlayCoreHasClavette ? "with multiple posts" : "";
  }
  return item.inlayCoreHasClavette ? "avec clavette" : "";
};

export const getItemShadeLabel = (item: Item) => {
  if ("itemShade" in item) {
    return item.itemShade?.label ?? "";
  }
  return item.teethShade?.label ?? "";
};

export const getItemInlayCoreScanPostLabel = (item: AnatomyItem) => {
  if (
    item.itemType === ItemType.INLAY_CORE ||
    item.itemType === ItemType.RICHMOND_CROWN ||
    item.itemType === ItemType.POST_AND_CORE_RETAINED_CROWN
  ) {
    const scanPostLabel =
      scanPosts.find(scanPost => scanPost.value === item.inlayCoreScanPost)
        ?.label ?? INLAY_CORE_SCAN_POST.ANATOMICAL.label;
    return scanPostLabel ? `Scan post ${scanPostLabel.toLowerCase()}` : "";
  }
  return "";
};

export const getItemInlayCoreScanPostName = (item: AnatomyItem): string =>
  item.inlayCoreScanPost === "ANATOMICAL"
    ? "Anatomique"
    : (scanPosts.find(scanPost => scanPost.value === item.inlayCoreScanPost)
        ?.label ?? item.inlayCoreScanPost);

export const getItemInlayCoreScanPostTranslated = (
  item: AnatomyItem,
  shouldTranslate: boolean = true
) => {
  if (
    item.itemType === ItemType.INLAY_CORE ||
    item.itemType === ItemType.RICHMOND_CROWN ||
    item.itemType === ItemType.POST_AND_CORE_RETAINED_CROWN
  ) {
    const scanPostLabel = shouldTranslate
      ? (scanPosts
          .find(scanPost => scanPost.value === item.inlayCoreScanPost)
          ?.value.replaceAll("_", " ") ?? item.inlayCoreScanPost)
      : getItemInlayCoreScanPostName(item);
    return `Scan post: ${scanPostLabel}`;
  }
  return "";
};

const RETENTION_TYPE_NAMES: Record<RetentionType, string> = {
  [RetentionType.CEMENT_RETAINED]: "scellé",
  [RetentionType.SCREW_RETAINED]: "transvissé",
};

export const getImplantRetentionTypeName = (item: ImplantItem): string =>
  RETENTION_TYPE_NAMES[item.retentionType] ?? "";

export const getImplantRetentionTypeLabel = (
  item: ImplantItem,
  shouldTranslate: boolean = false
) => {
  const retentionTypeName = getImplantRetentionTypeName(item);
  if (!retentionTypeName) {
    return "";
  }
  return shouldTranslate
    ? `Retention type: ${item.retentionType}`
    : `Système de rétention : ${retentionTypeName}`;
};

export const getItemAbutmentMaterialLabel = (
  item: ImplantItem,
  shouldTranslate: boolean = false
) => {
  if (shouldTranslate) {
    return translateItemMaterial(item?.abutmentMaterial?.id);
  }
  return item.abutmentMaterial?.label ?? "";
};

export const sortItems = (items: any, productType: ItemType) => {
  if (!items || items.length < 2) {
    return items;
  }

  if (
    productType === ItemType.INLAY_CORE ||
    productType === ItemType.CROWN ||
    productType === ItemType.LAY
  ) {
    items = [...items].sort((a, b) => {
      const aTooth = a?.teeth?.[0] ?? 0;
      const bTooth = b?.teeth?.[0] ?? 0;

      if (aTooth !== bTooth) {
        return aTooth - bTooth;
      }

      if (a.itemType === b.itemType) return 0;
      if (a.itemType === ItemType.INLAY_CORE) return -1;
      if (b.itemType === ItemType.INLAY_CORE) return 1;

      return 0;
    });
  }

  if (productType === ItemType.BRIDGE_IMPLANT) {
    items = [...items].sort(a => {
      if (a.itemType === ItemType.BRIDGE_IMPLANT) return -1;
      if (a.itemType === ItemType.IMPLANT) return 1;
      return 0;
    });
  }

  if (productType === ItemType.BRIDGE) {
    items = [...items].sort((a, b) => {
      const aIsBridge = a.itemType === ItemType.BRIDGE;
      const bIsBridge = b.itemType === ItemType.BRIDGE;

      if (aIsBridge === bIsBridge) {
        const aTooth = a?.teeth?.[0] ?? 0;
        const bTooth = b?.teeth?.[0] ?? 0;

        if (aTooth !== bTooth) {
          return aTooth - bTooth;
        }
      }

      return aIsBridge ? -1 : 1;
    });

    // Group identical same characteristics items together if they are following each other. Only materials can differ within the same product.
    items = items.reduce((acc: any[], item: any, index: number) => {
      if (index === 0) {
        return [item];
      }

      const lastItem = acc[acc.length - 1];

      // We group together bridge items if they have the same material and are following each other to avoid having multiple identical lines in the summary
      if (
        item.itemType === ItemType.BRIDGE &&
        item.itemType === lastItem.itemType &&
        item.itemMaterial?.id === lastItem.itemMaterial?.id
      ) {
        return [
          ...acc.slice(0, -1),
          {
            ...lastItem,
            teeth: [...(lastItem.teeth ?? []), ...(item.teeth ?? [])],
          },
        ];
      }
      return [...acc, item];
    }, []);
  }

  return items;
};

export const getItemAbutmentTypeLabel = (
  item: ImplantItem,
  shouldTranslate: boolean = false
) => {
  if (
    (item.itemType === ItemType.IMPLANT ||
      item.itemType === ItemType.SPLINTED_CROWN_IMPLANT) &&
    item.abutmentType
  ) {
    let abutmentLabel =
      AbutmentTypeChoices.find((a: any) => a.value === item.abutmentType) ??
      StandardAbutmentTypeChoices.find(
        (a: any) => a.value === item.abutmentType
      );
    abutmentLabel = shouldTranslate
      ? abutmentLabel?.value
      : abutmentLabel?.label;
    if (abutmentLabel) {
      return abutmentLabel.charAt(0).toUpperCase() + abutmentLabel.slice(1);
    }
  }
  return "";
};

export const getItemTypePluralLabel = (
  item: AnatomyItem | ImplantItem | RemovableItem
) => {
  if (!item || !item.itemType) return "";
  const plural =
    Array.isArray(item.teeth) && item.teeth.length > 1 ? true : false;
  if (
    !plural ||
    isRemovableItemType(item.itemType) ||
    item.itemType === ItemType.BRIDGE ||
    item.itemType === ItemType.BRIDGE_IMPLANT
  ) {
    return getItemTypeLabel(item.itemType);
  }
  if (item.itemType === ItemType.CROWN) {
    return "Couronnes";
  } else if (item.itemType === ItemType.INLAY) {
    return "Inlays";
  } else if (item.itemType === ItemType.IMPLANT) {
    return "Piliers";
  } else if (item.itemType === ItemType.SPLINTED_CROWN_IMPLANT) {
    return "Couronnes solidarisées sur implant";
  } else if (item.itemType === ItemType.IMPLANT_CROWN) {
    return "Couronnes sur implant";
  } else if (item.itemType === ItemType.ONLAY) {
    return "Onlays";
  } else if (item.itemType === ItemType.LAY) {
    return "Inlays/Onlays";
  } else if (item.itemType === ItemType.OVERLAY) {
    return "Overlay";
  } else if (item.itemType === ItemType.VENEER) {
    return "Facettes";
  } else if (item.itemType === ItemType.INLAY_CORE) {
    return "Inlay-cores";
  } else if (
    item.itemType === ItemType.POST_AND_CORE_RETAINED_CROWN ||
    item.itemType === ItemType.RICHMOND_CROWN
  ) {
    return "Couronnes Richmond";
  } else if (item.itemType === ItemType.SPLINTED_CROWN) {
    return "Couronnes solidarisées";
  } else if (item.itemType === ItemType.SPACE_MAINTAINER) {
    return "Mainteneurs d'espace";
  } else if (item.itemType === ItemType.RETENTION_WIRE) {
    return "Fils de contention";
  } else if (item.itemType === ItemType.WAX_UP) {
    return "Wax up";
  } else if (item.itemType === ItemType.CUSTOM_TRAY) {
    return "Porte-empreinte individuel";
  } else if (
    item.itemType === ItemType.ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS
  ) {
    return "Empreintes supplémentaires pour prothèse amovible";
  } else if (item.itemType === ItemType.ADDING_TEETH_ON_EXISTING_DENTURE) {
    return "Adjonction de dents sur prothèse existante";
  } else if (item.itemType === ItemType.DENTURE_REPAIR) {
    return "Réparation prothèse amovible";
  } else if (item.itemType === ItemType.ORTHO_MODEL) {
    return "Modèle étude ortho";
  } else if (item.itemType === ItemType.WAX_BITE) {
    return "Cires d'occlusion";
  } else {
    return "";
  }
};

export const getItemTypeWithCountLabel = (
  item: AnatomyItem | ImplantItem | RemovableItem,
  shouldTranslate: boolean = false
) => {
  if (!item || !item.itemType) {
    return "";
  }
  const itemName = shouldTranslate
    ? translateItemType(item.itemType)
    : getItemTypePluralLabel(item);
  if (item.itemType === ItemType.BRIDGE_IMPLANT) {
    return `- 1 x ${itemName}`;
  } else if (
    isRemovableItemType(item.itemType) ||
    item.itemType === ItemType.BRIDGE
  ) {
    if (item.itemType === ItemType.RETAINER && shouldTranslate) {
      return `- Clear ${itemName}`;
    }
    return `- ${itemName}`;
  } else if (item.itemType === ItemType.SPLINTED_CROWN) {
    return `- 1 x ${
      Array.isArray(item.teeth) ? item.teeth.length : 0
    } ${itemName}`;
  } else {
    return `- ${
      Array.isArray(item.teeth) ? item.teeth.length : 0
    } x ${itemName}`;
  }
};

export const getShadeBrandByShade = (
  allShades: { id: string; label: string; shadeType: string }[],
  selectedShade: string
) => {
  const shades =
    allShades !== null && allShades !== undefined ? [...allShades] : [];
  const matchingShade = shades.filter(shade => shade.id === selectedShade)[0];
  return matchingShade ? matchingShade.shadeType : undefined;
};

export const filterShadeByShadeType = (
  allShades: {
    id: string;
    label: string;
    shadeType: string;
  }[],
  selectedShadeType:
    FixedProsthesisShadeType | PartialAndFullDentureProsthesisShadeType
) => {
  const shades =
    allShades !== null && allShades !== undefined ? [...allShades] : [];
  if (!selectedShadeType) {
    return shades.map(shade => ({
      value: shade.id,
      label: shade.label,
    }));
  }
  return shades
    .filter(shade => shade.shadeType === selectedShadeType)
    .map(shade => ({ value: shade.id, label: shade.label }));
};

export const getImplantReferenceBrands = (data: {
  getAllImplantReferences: { brand: string }[];
}) => {
  return (data?.getAllImplantReferences ?? [])
    .map(implantReference => implantReference.brand.trim())
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort((a, b) => a.localeCompare(b));
};

export const getImplantReferenceChoices = (
  data: any
): {
  value: string;
  label: string;
  brand: string;
  platforms: string[];
}[] => {
  return (data?.getAllImplantReferences ?? []).map((implantReference: any) => ({
    value: implantReference.id,
    ...implantReference,
  }));
};

export const getImplantItemPlatformChoices = (data: string[]) => {
  if (data) {
    return data.map((item: string) => {
      return { label: item, value: item };
    });
  }
  return [];
};

export const getImplantItemConnectionChoices = (
  data: { value: string; label: string }[]
) => {
  if (data && data.length > 0) {
    return data.map((item: { value: string; label: string }) => {
      return { label: item.label, value: item.value };
    });
  }
  return [];
};

/**
 * Wax-up v2 persists one item per tooth and wax-up type: merge them back into
 * a single item covering the product teeth, as wax-up v1 stored it.
 */
export const mergeWaxUpItems = (
  items: RemovableItem[],
  productTeeth: number[]
): RemovableItem[] => {
  if (items.length < 2) {
    return items;
  }

  const waxUpType = getWaxUpTypeSelection(items) as WaxUpType | undefined;

  return [
    {
      ...items[0],
      teeth: productTeeth,
      waxUpType: waxUpType ?? items[0].waxUpType,
    },
  ];
};
